import { useState } from "react";
import { MessageCircle, Mail, Phone, MapPin, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const Contact = () => {
  const { t, lang } = useLanguage();
  const cp = t.contactPage;
  const [form, setForm] = useState({ name: "", email: "", topic: cp.form.topics[0], message: "" });
  const [error, setError] = useState("");

  const buildMessage = () => `${cp.form.name}: ${form.name}\n${cp.form.email}: ${form.email}\n${cp.form.topic}: ${form.topic}\n\n${form.message}`;

  const validate = () => {
    if (!form.name.trim() || !form.message.trim()) {
      setError(cp.form.nameRequired);
      return false;
    }
    setError("");
    return true;
  };

  const sendEmail = () => {
    if (!validate()) return;
    const subject = encodeURIComponent(`marinaknows.com — ${form.topic} — ${form.name}`);
    const body = encodeURIComponent(buildMessage());
    window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
  };

  const sendWhatsapp = () => {
    if (!validate()) return;
    window.open(`${CONTACT.whatsapp}?text=${encodeURIComponent(buildMessage())}`, "_blank", "noopener,noreferrer");
  };

  const field = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const inputCls =
    "w-full bg-transparent border-b border-ink/25 focus:border-terracotta outline-none py-3 text-ink placeholder:text-stone-fg transition-colors";

  return (
    <div data-testid="contact-page">
      <Seo
        title={lang === "es" ? "Contacto — Marina Knows · Valencia" : "Contact — Marina Knows · Valencia"}
        description={lang === "es"
          ? "Contacta con Marina por WhatsApp, email o teléfono. En persona en la Comunidad Valenciana (Valencia, Alicante, Xàbia, Dénia, Gandía) y online en toda España."
          : "Reach Marina by WhatsApp, email or phone. In person across the Comunidad Valenciana (Valencia, Alicante, Xàbia, Dénia, Gandía) and online Spain-wide."}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">{cp.overline}</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-ink mb-6" data-testid="contact-title">{cp.title}</h1>
        <p className="text-lg text-ink/70 max-w-2xl mb-16" data-testid="contact-sub">{cp.sub}</p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: contact methods + area */}
          <div className="lg:col-span-5 space-y-8">
            <Reveal>
              <div className="space-y-4">
                <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
                   className="flex items-center gap-4 bg-white border border-stone rounded-2xl p-6 hover:border-terracotta/50 hover:-translate-y-0.5 transition-all">
                  <span className="bg-terracotta text-ink p-3 rounded-full"><MessageCircle size={20} aria-hidden /></span>
                  <span>
                    <span className="block font-medium text-ink">{cp.whatsapp}</span>
                    <span className="block text-stone-fg text-sm">{CONTACT.phoneDisplay}</span>
                  </span>
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email"
                   className="flex items-center gap-4 bg-white border border-stone rounded-2xl p-6 hover:border-terracotta/50 hover:-translate-y-0.5 transition-all">
                  <span className="bg-azul text-white p-3 rounded-full"><Mail size={20} aria-hidden /></span>
                  <span>
                    <span className="block font-medium text-ink">{cp.emailLabel}</span>
                    <span className="block text-stone-fg text-sm">{CONTACT.email}</span>
                  </span>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone"
                   className="flex items-center gap-4 bg-white border border-stone rounded-2xl p-6 hover:border-terracotta/50 hover:-translate-y-0.5 transition-all">
                  <span className="bg-ink text-sand p-3 rounded-full"><Phone size={20} aria-hidden /></span>
                  <span>
                    <span className="block font-medium text-ink">{cp.phoneLabel}</span>
                    <span className="block text-stone-fg text-sm">{CONTACT.phoneDisplay}</span>
                  </span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="bg-cream border border-stone rounded-2xl p-7">
                <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-olive mb-4">
                  <MapPin size={14} aria-hidden /> {cp.areaTitle}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {t.home.areas.map((area, i) => (
                    <span key={i} data-testid={`contact-area-${i}`} className="text-sm border border-azul/40 text-azul-dark px-3 py-1.5 rounded-full">
                      {area}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-stone-fg mb-5">{cp.areaSub}</p>
                <div className="rounded-xl overflow-hidden border border-stone">
                  <iframe
                    title="Comunidad Valenciana service area map"
                    data-testid="area-map"
                    src="https://www.openstreetmap.org/export/embed.html?bbox=-1.20%2C37.85%2C0.60%2C40.15&layer=mapnik&marker=39.47%2C-0.38"
                    className="w-full h-64"
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="bg-white border border-stone rounded-2xl p-8 md:p-10" data-testid="contact-form">
              <div className="space-y-7">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="cf-name" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">{cp.form.name}</label>
                    <input id="cf-name" data-testid="contact-form-name" type="text" value={form.name}
                      onChange={(e) => field("name", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">{cp.form.email}</label>
                    <input id="cf-email" data-testid="contact-form-email" type="email" value={form.email}
                      onChange={(e) => field("email", e.target.value)} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="cf-topic" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">{cp.form.topic}</label>
                  <select id="cf-topic" data-testid="contact-form-topic" value={form.topic}
                    onChange={(e) => field("topic", e.target.value)}
                    className={`${inputCls} bg-white cursor-pointer`}>
                    {cp.form.topics.map((topic, i) => (
                      <option key={i} value={topic}>{topic}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-message" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">{cp.form.message}</label>
                  <textarea id="cf-message" data-testid="contact-form-message" rows={5} value={form.message}
                    placeholder={cp.form.messagePlaceholder}
                    onChange={(e) => field("message", e.target.value)} className={`${inputCls} resize-none`} />
                </div>

                {error && (
                  <p className="text-bronze text-sm font-medium" data-testid="contact-form-error">{error}</p>
                )}

                <div className="flex flex-wrap gap-4 pt-2">
                  <button onClick={sendWhatsapp} data-testid="contact-send-whatsapp"
                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
                    <MessageCircle size={17} aria-hidden /> {cp.form.sendWhatsapp}
                  </button>
                  <button onClick={sendEmail} data-testid="contact-send-email"
                    className="inline-flex items-center gap-2 border border-ink/20 hover:border-terracotta hover:text-bronze text-ink font-medium px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
                    <Send size={16} aria-hidden /> {cp.form.sendEmail}
                  </button>
                </div>
                <p className="text-sm text-stone-fg">{cp.form.note}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
